import { Link } from "react-router";
import {
  RocketOutlined,
  TeamOutlined,
  ClusterOutlined,
  FundOutlined,
  BulbOutlined,
  UserOutlined,
  CheckOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import "../HomePage/HomePage.css"; // shared landing primitives (badge, btn, cta, tokens)
import "./SolutionsPage.css";

const SEGMENTS = [
  {
    accent: "teal",
    icon: <RocketOutlined />,
    title: "VC-Backed Startups",
    label: "Seed → Series A",
    popular: true,
    desc: "Build credible, investor-grade financials to raise capital and make growth decisions with conviction.",
    points: [
      "Industry-specific model",
      "Investor-ready projections",
      "Valuation & dilution",
      "Auto-generated investor reports",
    ],
  },
  {
    accent: "gold",
    icon: <TeamOutlined />,
    title: "Fractional CFOs & Consultants",
    label: "FP&A startup advisors",
    desc: "Deliver FP&A faster and more consistently across every client. One standardized playbook, 10× the throughput.",
    points: ["Multi-client workspace", "Standardized templates", "Volume pricing", "White-labeled reports"],
  },
  {
    accent: "coral",
    icon: <ClusterOutlined />,
    title: "Venture Studios & Accelerators",
    label: "Portfolio-wide consistency",
    desc: "Standardize investor-ready financials across your entire portfolio. Consistent outputs, consistent story.",
    points: ["Portfolio dashboards", "Consistent frameworks", "Founder onboarding", "Cohort benchmarking"],
  },
  {
    accent: "teal",
    icon: <FundOutlined />,
    title: "VC & Investment Teams",
    label: "Internal IC intelligence",
    desc: "Evaluate deals, stress-test models, and run valuation + dilution scenarios.",
    points: ["Deal evaluation", "Stress-test scenarios", "Standardized assumptions", "IC-ready memos"],
  },
  {
    accent: "gold",
    icon: <BulbOutlined />,
    title: "Corporate Innovation Teams",
    label: "New ventures & capital allocation",
    desc: "Model new ventures and capital allocation under uncertainty — at startup speed, without enterprise FP&A overhead.",
    points: ["New-venture models", "Scenario planning", "Board-ready outputs", "Multi-user access"],
  },
  {
    accent: "coral",
    icon: <UserOutlined />,
    title: "Solo Founders & Pre-Seed",
    label: "First credible model",
    desc: "Build your first credible financial model and pitch support — fast, guided, and investor-ready from day one.",
    points: ["Guided projections", "Starter model", "Basic scenarios", "Pitch support"],
  },
];

const CAPABILITIES = [
  {
    n: "01",
    accent: "teal",
    title: "Budgeting & forecasting",
    desc: "Create accurate financial projections and annual budgets aligned with business goals. Industry-tailored starter models (60+ line items) so you're never staring at a blank spreadsheet.",
    tags: ["60+ line items", "Growth metrics", "Product revenues", "Industry tailored"],
  },
  {
    n: "02",
    accent: "gold",
    title: "Performance dashboards & KPI tracking",
    desc: "Real-time visibility into financial and operational performance. 25+ firm-wide and product KPIs tracked automatically — P&L, cash, runway, unit economics, product analysis.",
    tags: ["25+ KPIs", "Product economics", "Customer analytics", "Unit economics"],
  },
  {
    n: "03",
    accent: "coral",
    title: "Scenario & sensitivity planning",
    desc: "Model what-if situations for growth, cost-efficiency, industry-drivers, and market changes. Sensitivity analysis on every key driver into your financials.",
    tags: ["Growth metrics", "Cost structure", "Investment drivers", "Venture-Scale"],
  },
  {
    n: "04",
    accent: "teal",
    title: "Strategic planning & investor reporting",
    desc: "Translate business strategy into financial models and reports for leadership and stakeholders. Auto-generated investor updates and templates.",
    tags: ["Equity Management", "Investor updates", "Business Strategy", "Funding & Pricing"],
  },
];

function SolutionsPage() {
  return (
    <div className="home pn-landing pn-solutions pn-bg-textured">
      {/* ===== HERO ===== */}
      <section className="pn-sol-hero">
        <div className="pn-hero-glow" />
        <div className="pn-container pn-sol-hero-inner">
          <span className="pn-badge">
            <i className="pn-badge-dot" /> Solutions
          </span>
          <h1 className="pn-sol-title">
            One platform. <span className="pn-gold">Six ways</span> to win.
          </h1>
          <p className="pn-sol-sub">
            Whether you're raising your seed, advising 20 clients, or stress-testing an IC memo —
            PreNumbers gives you investor-grade financial intelligence in minutes. We are your single
            source of truth.
          </p>
          <div className="pn-sol-hero-cta">
            <Link to="/contact-sales" className="pn-btn pn-btn-gold">
              Request a demo <ArrowRightOutlined />
            </Link>
            <Link to="/pricing" className="pn-btn pn-btn-dark">
              See pricing
            </Link>
          </div>
        </div>
      </section>

      {/* ===== SEGMENTS ===== */}
      <section className="pn-sol-segments">
        <div className="pn-container pn-seg-grid">
          {SEGMENTS.map((s) => (
            <div className={`pn-seg-card ${s.accent}`} key={s.title}>
              <div className="pn-seg-head">
                <span className="pn-seg-icon">{s.icon}</span>
                <div className="pn-seg-titles">
                  <h3>{s.title}</h3>
                  <span className="pn-seg-label">{s.label}</span>
                </div>
                {s.popular && <span className="pn-seg-pop">Most popular</span>}
              </div>
              <p className="pn-seg-desc">{s.desc}</p>
              <ul className="pn-seg-points">
                {s.points.map((p) => (
                  <li key={p}>
                    <CheckOutlined /> {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CAPABILITIES ===== */}
      <section className="pn-sol-cap">
        <div className="pn-container">
          <span className="pn-badge pn-badge-center">
            <i className="pn-badge-dot" /> What you get
          </span>
          <h2 className="pn-section-title">Core FP&amp;A capabilities</h2>
          <p className="pn-section-sub">Everything from day one — in a single, coherent workspace.</p>
          <div className="pn-cap-list">
            {CAPABILITIES.map((c) => (
              <div className={`pn-cap-row ${c.accent}`} key={c.n}>
                <div className="pn-cap-main">
                  <div className="pn-cap-num">
                    <span className="pn-cap-n">{c.n}</span>
                    <i className="pn-cap-dot" />
                  </div>
                  <div>
                    <h3 className="pn-cap-title">{c.title}</h3>
                    <p className="pn-cap-desc">{c.desc}</p>
                  </div>
                </div>
                <div className="pn-cap-tags">
                  {c.tags.map((t) => (
                    <span className="pn-tag" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="pn-sol-cta">
        <div className="pn-container">
          <div className="pn-cta-card">
            <div className="pn-cta-content">
              <span className="pn-badge">
                <i className="pn-badge-dot" /> Get started
              </span>
              <h2 className="pn-cta-title">
                See investor-grade FP&amp;A in <span className="pn-gold">minutes.</span>
              </h2>
              <p>
                A PreNumbers specialist walks you through an example of industry tailored model for
                your venture — projections, valuation, cap table, scenarios — live.
              </p>
            </div>
            <div className="pn-cta-actions">
              <Link to="/contact-sales" className="pn-btn pn-btn-gold">
                Request a demo <ArrowRightOutlined />
              </Link>
              <Link to="/pricing" className="pn-btn pn-btn-ghost">
                See pricing
              </Link>
              <span className="pn-cta-note">No credit card · 20-min call · investor-grade output</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SolutionsPage;
